<script setup>
import { computed } from 'vue';
import { useGlobalStore } from '@/stores/global';
import { formApi } from '@/configs/forms-api';

const props = defineProps({
  buttons: { type: Array, default: () => [] },
  backgroundColor: { type: String, default: '' },
  template: { type: String, default: '' },
  maintenanceData: {
    type: Object,
    default: () => {},
  },
  buttonsPrefixClass: { type: String, default: '' },
});

const maintenanceCalculatorData = computed(() => props.maintenanceData);
const globalStore = useGlobalStore();

const fileExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'zip'];

const hasLinkUrl = (btn) => {
  const url = btn?.link?.url;
  return typeof url === 'string' && url.trim().length > 0;
};

const isPhoneLink = (btn) =>
  btn?.type === 'link' && btn?.link?.url?.startsWith('tel:');

// Функція для визначення target
const getButtonTarget = (btn) => {
  if (isPhoneLink(btn)) return '_blank';
  return btn.link?.target;
};

const isFileLink = (url) =>
  !!url && fileExtensions.some((ext) => url.toLowerCase().endsWith(`.${ext}`));

const isFileButton = (btn) =>
  btn?.type === 'link' && hasLinkUrl(btn) && isFileLink(btn.link.url);

const nonFileIndexByButtonIndex = computed(() => {
  let n = -1;

  return props.buttons.map((btn) => {
    if (btn?.type === 'link' && !hasLinkUrl(btn)) return null;
    if (isFileButton(btn)) return null;

    n += 1;
    return n;
  });
});

const colorClass = (btn, index) => {
  if (isFileButton(btn)) {
    return props.backgroundColor === 'black' ? 'white' : 'black';
  }

  const nfi = nonFileIndexByButtonIndex.value[index] ?? 0;
  const isPrimary = nfi % 2 === 0;

  if (props.backgroundColor === 'black') {
    return isPrimary || props.template === 'header'
      ? 'button-white'
      : props.template === 'equipment'
        ? ''
        : 'button-transparent-white';
  }

  if (props.template === 'header') {
    return 'button-white';
  }
  if ((props.template === 'stock' && index === 2) || isPhoneLink(btn)) {
    return 'button-transparent';
  }
  return isPrimary
    ? 'button-black'
    : props.template === 'equipment'
      ? ''
      : 'button-transparent';
};

const iconColor = (btn, index) => {
  if (isFileButton(btn)) {
    return props.backgroundColor === 'black' ? 'white' : 'black';
  }
  if (props.template === 'stock' && index === 2) {
    return 'black';
  }
  const nfi = nonFileIndexByButtonIndex.value[index] ?? 0;
  const isPrimary = nfi % 2 === 0;

  if (props.backgroundColor === 'black') {
    return isPrimary ? 'black' : 'white';
  }
  return isPrimary ? 'white' : 'black';
};

function handleApplicationClick(type, services) {
  globalStore.setApplicationPopup({
    visible: true,
    type,
    maintenanceData: maintenanceCalculatorData,
    services: services,
  });
}

const hasDuplicateButtons = computed(() => {
  if (!props.buttons || props.buttons.length < 2) return false;
  const serializedButtons = props.buttons.map((btn) => JSON.stringify(btn));
  return new Set(serializedButtons).size < serializedButtons.length;
});

const getDynamicIndexClass = (baseClass, index) => {
  return hasDuplicateButtons.value ? `${baseClass}-${index}` : baseClass;
};
</script>

<template>
  <div v-if="buttons?.length" class="buttons">
    <template v-for="(button, index) in buttons" :key="index">
      <template v-if="button.title && button.title.trim().length > 0">
        <template
          v-if="
            button.type === 'link' &&
            button?.link?.url &&
            button?.link?.url?.length > 0
          "
        >
          <Button
            v-if="isFileButton(button)"
            v-slot="slotProps"
            as-child
            variant="text"
          >
            <a
              :target="getButtonTarget(button)"
              :href="button.link?.url"
              class="buttons__item link-normal button-icon-prev"
              :class="[
                slotProps.class,
                colorClass(button, index),
                getDynamicIndexClass(`${buttonsPrefixClass}-file`, index),
              ]"
            >
              <CIcon
                class="button-icon-prev-icon"
                :name="
                  backgroundColor === 'black' ? 'doc-load' : 'doc-load-black'
                "
                width="16"
                height="16"
              />
              {{ button.title }}
            </a>
          </Button>

          <template v-else>
            <Button
              v-if="template === 'equipment'"
              v-slot="slotProps"
              as-child
              text
              size="large"
            >
              <CTagDetect
                :target="getButtonTarget(button)"
                :to="button.link?.url"
                :class="[
                  slotProps.class,
                  colorClass(button, index),
                  getDynamicIndexClass(
                    `${buttonsPrefixClass}-equipment`,
                    index,
                  ),
                ]"
              >
                <CIcon
                  v-if="isPhoneLink(button)"
                  name="phone-red"
                  width="24"
                  height="24"
                  class="mr-2"
                />
                {{ button.title }}
              </CTagDetect>
            </Button>

            <Button v-else v-slot="slotProps" as-child rounded>
              <CTagDetect
                :target="getButtonTarget(button)"
                :to="button.link?.url"
                class="buttons__item link-normal"
                :class="[
                  slotProps.class,
                  colorClass(button, index),
                  isPhoneLink(button)
                    ? `button-prev-arrow button--without-borders ${getDynamicIndexClass(`${buttonsPrefixClass}-${button.link?.url}`, index)}`
                    : template === 'main'
                      ? `button-prev-arrow ${getDynamicIndexClass(`${buttonsPrefixClass}-prev`, index)}`
                      : `button-next-arrow ${getDynamicIndexClass(`${buttonsPrefixClass}-next`, index)}`,
                ]"
              >
                <CIcon
                  v-if="template === 'main'"
                  :name="
                    iconColor(button, index) === 'white'
                      ? 'arrow-white'
                      : 'arrow-black'
                  "
                  width="16"
                  height="16"
                  class="button-prev-arrow__icon"
                />
                <CIcon
                  v-if="isPhoneLink(button)"
                  name="phone-red"
                  width="24"
                  height="24"
                  class="mr-2"
                />
                {{ button.title }}
                <CIcon
                  v-if="template != 'main' && !isPhoneLink(button)"
                  :name="
                    iconColor(button, index) === 'white'
                      ? 'arrow-white'
                      : 'arrow-black'
                  "
                  width="16"
                  height="16"
                  class="button-next-arrow__icon"
                />
              </CTagDetect>
            </Button>
          </template>
        </template>

        <template v-else-if="button.type === 'popup'">
          <Button
            rounded
            class="buttons__item link-normal"
            :class="[
              colorClass(button, index),
              getDynamicIndexClass(
                `${buttonsPrefixClass}-${formApi[button.popup]}`,
                index,
              ),
            ]"
            @click="handleApplicationClick(button.popup, button.services)"
          >
            {{ button.title }}
          </Button>
        </template>
      </template>
    </template>
  </div>
</template>
<style scoped lang="scss">
.buttons {
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 8px;
  @include media-breakpoint-up(sm) {
    gap: 16px;
    flex-direction: row;
  }
  @include media-breakpoint-up(md) {
    margin-top: auto;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
}
</style>
